/** @format */

import { AnalyticsService, DynamicTemplateLoader, WINDOW } from '@shared/data-access/services';
import {
	BrandStyliticBundleConfig,
	BrandStyliticConfig,
	StyliticsBundleConfig,
	StyliticsElementItem,
	StyliticsItem,
	StyliticsOptions,
	StyliticsWidget
} from '@pdp/data-access/interfaces';
import { Chain, Environment, ProcessedProduct } from '@shared/data-access/interfaces';
import {
	Component,
	ComponentRef,
	ElementRef,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Renderer2,
	SimpleChanges,
	inject,
	input
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HmfBadgeComponent } from '@dsg-tech/homefield-angular';
import { Store } from '@ngrx/store';
import { setStyliticsWidgetShown } from '@pdp/data-access/state';
import { styliticsConfig } from './stylitics-constants';

@Component({
	selector: 'pdp-component-stylitics',
	templateUrl: './stylitics.component.html',
	styleUrl: './stylitics.component.scss',
	standalone: true,
	imports: [CommonModule]
})
export class StyliticsComponent implements OnInit, OnChanges, OnDestroy {
	private readonly analyticsService = inject(AnalyticsService);
	readonly chain = inject(Chain);
	readonly environment = inject(Environment);
	private readonly elementRef = inject(ElementRef);
	protected readonly window = inject<Window>(WINDOW);
	private readonly renderer = inject(Renderer2);
	private readonly document = inject<Document>(DOCUMENT);
	private readonly dynamicTemplateLoader = inject(DynamicTemplateLoader);
	private readonly ngZone = inject(NgZone);
	private readonly store = inject(Store);
	private readonly platformId = inject(PLATFORM_ID);

	/** @description Stylitics Kill switch */
	readonly styliticsEnabled = input.required<boolean>();

	/** @description Enable/Disable Badge for "Model Is Wearing" */
	readonly shopTheLookEnabled = input.required<boolean>();
	/** @description The current Product */
	readonly product = input.required<ProcessedProduct>();

	/** @description The selected color for the product */
	readonly selectedColorAttribute = input<string>();

	/** @description Enable/Disable Stylitics MnM */
	readonly styliticsMnMEnabled = input.required<boolean>();

	/** @description  Show/Hide Complete The Set Header */
	showHeader: boolean;

	/** @description  The stylitics widget instance */
	private styliticsWidget: StyliticsWidget;

	private subscription: Subscription;

	private lazyHmfBadge: ComponentRef<HmfBadgeComponent> | undefined;

	/** @description The chain identifier abbreviation */
	get chainIdentifier(): string {
		return this.chain.chainIdentifierAbbr;
	}

	/** @description The chain configuration object */
	get chainConfig(): BrandStyliticConfig {
		return styliticsConfig[this.chainIdentifier];
	}

	ngOnInit() {
		// Killswitch is being pulled from the in-stock-default component
		if (isPlatformBrowser(this.platformId) && this.chainConfig && this.styliticsEnabled()) {
			this.ngZone.runOutsideAngular(() => {
				this.initializeStylitics();
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				this.subscription = this.observeDomChanges().subscribe((mutation) => {
					const mobileModalScrollElement =
						this.document.querySelector('.stylitics-modal-scroll');
					const desktopModalScrollElement = this.document.querySelector(
						'.stylitics-desktop-modal'
					);
					if (mobileModalScrollElement || desktopModalScrollElement) {
						this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
					} else {
						this.renderer.setStyle(this.document.body, 'overflow', 'scroll');
					}
				});
			});
		}
	}

	/**
	 * @description Refreshes stylitics widget whenever a different color swatch is selected.
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (
			changes.selectedColorAttribute &&
			changes.selectedColorAttribute.previousValue !== undefined &&
			this.styliticsWidget
		) {
			const currentSelectedColor = changes.selectedColorAttribute.currentValue;
			const productId = this.product().partNumber;
			const itemNumber = currentSelectedColor
				? `${productId}_${currentSelectedColor}`
				: `${productId}`;
			if (changes.selectedColorAttribute.previousValue !== currentSelectedColor) {
				this.styliticsWidget.refresh({
					api: {
						item_number: itemNumber.toUpperCase()
					}
				});
			}
		}
	}

	/**
	 * @description Initializes the stylitics widget and calls
	 * private helper method to attach event handlers.
	 */
	private initializeStylitics() {
		const styliticsScript = document.createElement('script');
		styliticsScript.type = 'text/javascript';
		styliticsScript.src =
			'https://web-assets.stylitics.com/v3-classic/latest/classic.release.js';

		this.elementRef.nativeElement.appendChild(styliticsScript).onload = () => {
			this.onLoadStylitics();
		};
	}

	/**
	 * @description Handles the loading of the stylitics widget.
	 */
	onLoadStylitics() {
		// eslint-disable-next-line no-constant-binary-expression, valid-typeof
		if (typeof window.StyliticsClassicWidget !== undefined) {
			const styliticsOptions = this.getStyliticsOptions();
			this.styliticsWidget = new window.StyliticsClassicWidget(
				this.chainConfig.name,
				'pdp-stylitics-target',
				styliticsOptions
			);
			this.setupStyliticsEvents(this.styliticsWidget);

			if (this.shopTheLookEnabled()) {
				this.styliticsWidget.on('mount', 'outfit', (data: StyliticsElementItem) => {
					this.setupModelOutfitBadge(data);
				});
			}

			// render the widget to its target div
			this.styliticsWidget.start();
		}
	}

	/**
	 * @description Returns the stylitics options object to be passed to the widget.
	 * @return {Object} The stylitics options object.
	 */
	getStyliticsOptions(): StyliticsOptions {
		const productId = this.product().partNumber;
		const itemNumber = this.selectedColorAttribute()
			? `${productId}_${this.selectedColorAttribute()}`
			: `${productId}`;
		const viewDetailsCTA = this.shopTheLookEnabled() ? 'Shop This Set' : 'Shop The Set';
		return {
			api: {
				min: 3,
				max: 3,
				item_number: itemNumber.toUpperCase()
			},
			text: {
				viewDetailsCTA,
				itemLinkCTA: 'SHOP'
			},
			display: {
				disableMnM: !this.styliticsMnMEnabled()
			}
		};
	}

	/**
	 * @description Sets up event handlers for the stylitics widget.
	 */
	private setupStyliticsEvents(styliticsWidget: StyliticsWidget) {
		styliticsWidget.on('mount', 'bundles', (data: StyliticsBundleConfig) => {
			this.setupStyliticsCSS(data);
			this.store.dispatch(
				setStyliticsWidgetShown({
					styliticsWidgetShown: this.shopTheLookEnabled() && !data.hidden
				})
			);
		});

		styliticsWidget.on('click', 'bundle', (item: StyliticsElementItem) => {
			// fires when the bundle is clicked
			this.analyticsService.triggerAnalyticsEvent('styliticsSTLClicked', item);
		});

		// pass the target (item or bundles):
		styliticsWidget.override('click', 'item', (item: { item: StyliticsItem }) => {
			const remoteId = item?.item?.remote_id ? item.item.remote_id.split('_')[0] : null;
			const itemColor = item?.item?.other_client_item_ids?.color;
			if (remoteId && itemColor) {
				this.analyticsService.triggerAnalyticsEvent('styliticsShopClicked', item);
				window.quickview.openProductQuickView(remoteId, 'stylitics', {
					defaultColor: itemColor
				});
			} else if (remoteId) {
				this.analyticsService.triggerAnalyticsEvent('styliticsShopClicked', item);
				window.quickview.openProductQuickView(remoteId, 'stylitics');
			}
		});
	}

	/**
	 * @description Querys the DOM to retrieve stylitics header and
	 * component to apply css styles to it based on the chain and
	 * defined styles within the constants file.
	 */
	private setupStyliticsCSS(data: StyliticsBundleConfig) {
		const styliticsElement = document.getElementById('pdp-stylitics-component');
		if (styliticsElement) {
			if (data.bundles?.length) {
				this.showHeader = true;
				this.applyStylesAndClasses(styliticsElement, this.chainConfig.bundles);
			} else {
				this.showHeader = false;
				this.applyStylesAndClasses(styliticsElement, this.chainConfig.nonBundles);
			}
		}
	}

	private applyStylesAndClasses(element: HTMLElement, config: BrandStyliticBundleConfig) {
		Object.assign(element.style, config.componentStyle);
		if (config.componentClassesToAdd) {
			element.classList.add(config.componentClassesToAdd);
		}
		if (config.componentClassesToRemove) {
			element.classList.remove(config.componentClassesToRemove);
		}
	}

	/**
	 * @description Adds an hmf-badge to the first bundle in the stylitics widget.
	 */
	setupModelOutfitBadge(data: StyliticsElementItem) {
		const outfit = data.outfit;
		if (outfit['on-model-image']) {
			const styliticsBadgeElement = data.element?.querySelector(
				'.stylitics-bundle-badge:not(.stylitics-bundle-badge-modal)'
			);
			if (styliticsBadgeElement) {
				this.mountLazyHmfBadge(styliticsBadgeElement);
			}
		}
	}

	mountLazyHmfBadge(element: Element) {
		if (!this.lazyHmfBadge) {
			this.lazyHmfBadge = this.dynamicTemplateLoader.createPlainDOMComponent(
				HmfBadgeComponent,
				{ badgeName: 'ModelWearing' }
			);
		}
		element.appendChild(this.lazyHmfBadge.location.nativeElement);
	}

	private observeDomChanges(): Observable<MutationRecord[]> {
		return new Observable((observer) => {
			const callback = (mutationsList: MutationRecord[]) => {
				observer.next(mutationsList);
			};

			const observerInstance = new MutationObserver(callback);
			const config = { childList: true, subtree: true };

			observerInstance.observe(this.document.body, config);

			// Cleanup function
			return () => observerInstance.disconnect();
		});
	}

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}

		if (this.lazyHmfBadge) {
			this.lazyHmfBadge.destroy();
		}
	}
}
